import { useState, useEffect } from "react";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import { Buildings, Plus, Users, Folder, Truck, Crown, Pencil, Power, MagnifyingGlass, CurrencyDollar, ShieldCheck, Clock, Lightning, Pulse, WarningCircle, SortAscending, Heartbeat } from "@phosphor-icons/react";

const PLAN_COLORS = {
  starter: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  professional: "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300",
  enterprise: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  one_time_licence: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
};
const PLAN_LABELS = { starter: "Starter", professional: "Professional", enterprise: "Enterprise", one_time_licence: "One-Time Licence" };
const STATUS_COLORS = {
  active: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  suspended: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  cancelled: "bg-gray-100 text-gray-700 dark:bg-gray-900/40 dark:text-gray-300",
  trialing: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
};
const ACTIVITY_BADGE = {
  active: { label: "Active", color: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300", icon: Pulse },
  dormant: { label: "Dormant", color: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300", icon: Clock },
  at_risk: { label: "At Risk", color: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300", icon: WarningCircle },
  new: { label: "New", color: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300", icon: Lightning },
};
const PLAN_LIMITS = {
  starter: { users: 5, projects: 10, suppliers: 25 },
  professional: { users: 20, projects: 50, suppliers: 100 },
  enterprise: { users: -1, projects: -1, suppliers: -1 },
  one_time_licence: { users: -1, projects: -1, suppliers: -1 },
};

function UsageBar({ used, limit, label }) {
  const unlimited = limit === -1;
  const pct = unlimited ? 0 : limit > 0 ? Math.min(100, (used / limit) * 100) : 0;
  return (
    <div className="text-[10px]">
      <div className="flex justify-between mb-0.5">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-semibold">{used}{unlimited ? "" : ` / ${limit}`}</span>
      </div>
      {!unlimited && <Progress value={pct} className="h-1" />}
    </div>
  );
}

function timeAgo(dateStr) {
  if (!dateStr) return "Never";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return new Date(dateStr).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

export default function TenantsPage() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterPlan, setFilterPlan] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterActivity, setFilterActivity] = useState("all");
  const [sortBy, setSortBy] = useState("created_at");
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", slug: "", plan: "starter" });
  const [editTenant, setEditTenant] = useState(null);
  const [healthMap, setHealthMap] = useState({});
  const [availablePlans, setAvailablePlans] = useState([]);

  const fetchTenants = () => {
    API.get("/platform/tenants-activity")
      .then(({ data }) => setTenants(data))
      .catch(() => {
        API.get("/tenants").then(({ data }) => setTenants(data)).catch(() => toast.error("Failed to load tenants"));
      })
      .finally(() => setLoading(false));
  };

  const fetchHealth = () => {
    API.get("/platform/tenant-health").then(({ data }) => {
      const map = {};
      (data.tenants || []).forEach(t => { map[t.tenant_id] = t.health; });
      setHealthMap(map);
    }).catch(() => {});
  };

  useEffect(() => {
    fetchTenants();
    fetchHealth();
    API.get("/billing/plans").then(({ data }) => setAvailablePlans(data)).catch(() => {});
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name || !form.slug) return toast.error("Name and slug required");
    setCreating(true);
    try {
      await API.post("/tenants", form);
      toast.success(`Tenant "${form.name}" created`);
      setShowCreate(false);
      setForm({ name: "", slug: "", plan: "starter" });
      fetchTenants();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to create tenant"); }
    finally { setCreating(false); }
  };

  const handleUpdate = async () => {
    if (!editTenant) return;
    try {
      await API.put(`/tenants/${editTenant.id}`, { name: editTenant.name, plan: editTenant.plan, status: editTenant.status });
      toast.success("Tenant updated");
      setEditTenant(null);
      fetchTenants();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to update"); }
  };

  const toggleStatus = async (tenant, newStatus) => {
    try {
      await API.put(`/tenants/${tenant.id}/status`, { status: newStatus });
      toast.success(`Tenant ${newStatus}`);
      fetchTenants();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  // Filtering
  let filtered = tenants.filter(t => {
    if (search && !t.name.toLowerCase().includes(search.toLowerCase()) && !t.slug.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterPlan !== "all" && t.plan !== filterPlan) return false;
    if (filterStatus !== "all" && t.status !== filterStatus) return false;
    if (filterActivity !== "all" && t.activity_status !== filterActivity) return false;
    return true;
  });

  // Sorting
  filtered = [...filtered].sort((a, b) => {
    if (sortBy === "name") return a.name.localeCompare(b.name);
    if (sortBy === "users") return (b.usage?.users || 0) - (a.usage?.users || 0);
    if (sortBy === "last_active") return (b.last_active || "").localeCompare(a.last_active || "");
    return (b.created_at || "").localeCompare(a.created_at || "");
  });

  const totalUsers = tenants.reduce((s, t) => s + (t.usage?.users || 0), 0);
  const activeTenants = tenants.filter(t => t.status === "active").length;
  const atRiskCount = tenants.filter(t => t.activity_status === "at_risk").length;
  const dormantCount = tenants.filter(t => t.activity_status === "dormant").length;

  if (loading) return <div className="text-center py-20 text-muted-foreground">Loading tenants...</div>;

  return (
    <div data-testid="tenants-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-5">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-indigo-600 to-purple-600 flex items-center justify-center">
            <Crown size={18} className="text-white" weight="fill" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold font-[Chivo] tracking-tight">SaaS Tenant Management</h1>
            <p className="text-xs text-muted-foreground">{tenants.length} tenant{tenants.length !== 1 ? "s" : ""} &middot; {activeTenants} active &middot; {totalUsers} users</p>
          </div>
        </div>
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogTrigger asChild>
            <Button size="sm" data-testid="create-tenant-btn"><Plus size={14} className="mr-1" /> New Tenant</Button>
          </DialogTrigger>
          <DialogContent data-testid="create-tenant-dialog">
            <DialogHeader><DialogTitle>Create New Tenant</DialogTitle></DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <Label>Company Name</Label>
                <Input value={form.name} onChange={e => { setForm({ ...form, name: e.target.value, slug: e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-+$/, "") }); }} className="mt-1" data-testid="tenant-name-input" required />
              </div>
              <div>
                <Label>Slug (subdomain)</Label>
                <div className="flex items-center gap-1 mt-1">
                  <Input value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })} className="flex-1" data-testid="tenant-slug-input" required />
                  <span className="text-xs text-muted-foreground">.necos.fm</span>
                </div>
              </div>
              <div>
                <Label>Plan</Label>
                <Select value={form.plan} onValueChange={v => setForm({ ...form, plan: v })}>
                  <SelectTrigger className="mt-1" data-testid="tenant-plan-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {availablePlans.length > 0 ? availablePlans.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    )) : (
                      <>
                        <SelectItem value="starter">Starter</SelectItem>
                        <SelectItem value="professional">Professional</SelectItem>
                        <SelectItem value="enterprise">Enterprise</SelectItem>
                        <SelectItem value="one_time_licence">One-Time Licence</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" className="w-full" disabled={creating} data-testid="create-tenant-submit">
                {creating ? "Creating..." : "Create Tenant"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 mb-6" data-testid="tenant-kpis">
        <div className="border border-border rounded-md p-3 bg-card">
          <Buildings size={18} className="text-indigo-500 mb-1.5" weight="duotone" />
          <p className="text-xl font-bold font-[Chivo]">{tenants.length}</p>
          <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Total Tenants</p>
        </div>
        <div className="border border-border rounded-md p-3 bg-card">
          <Users size={18} className="text-blue-500 mb-1.5" weight="duotone" />
          <p className="text-xl font-bold font-[Chivo]">{totalUsers}</p>
          <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Total Users</p>
        </div>
        <div className="border border-border rounded-md p-3 bg-card">
          <Pulse size={18} className="text-emerald-500 mb-1.5" weight="duotone" />
          <p className="text-xl font-bold font-[Chivo]">{tenants.filter(t => t.activity_status === "active").length}</p>
          <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Active (7d)</p>
        </div>
        <div className="border border-border rounded-md p-3 bg-card">
          <Clock size={18} className="text-amber-500 mb-1.5" weight="duotone" />
          <p className="text-xl font-bold font-[Chivo]">{dormantCount}</p>
          <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Dormant</p>
        </div>
        <div className="border border-border rounded-md p-3 bg-card">
          <WarningCircle size={18} className="text-red-500 mb-1.5" weight="duotone" />
          <p className="text-xl font-bold font-[Chivo]">{atRiskCount}</p>
          <p className="text-[9px] text-muted-foreground uppercase tracking-wider">At Risk</p>
        </div>
        <div className="border border-border rounded-md p-3 bg-card">
          <CurrencyDollar size={18} className="text-emerald-500 mb-1.5" weight="duotone" />
          <p className="text-xl font-bold font-[Chivo]">{tenants.filter(t => t.plan === "one_time_licence").length}</p>
          <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Licences</p>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="tenant-filters">
        <div className="relative flex-1 min-w-[180px]">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search tenants..." className="pl-8 h-8 text-xs" data-testid="tenant-search" />
        </div>
        <Select value={filterPlan} onValueChange={setFilterPlan}>
          <SelectTrigger className="w-32 h-8 text-xs" data-testid="filter-plan">
            <SelectValue placeholder="Plan" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Plans</SelectItem>
            {availablePlans.map(p => (
              <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-28 h-8 text-xs" data-testid="filter-status">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="suspended">Suspended</SelectItem>
            <SelectItem value="trialing">Trialing</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterActivity} onValueChange={setFilterActivity}>
          <SelectTrigger className="w-28 h-8 text-xs" data-testid="filter-activity">
            <SelectValue placeholder="Activity" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Activity</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="dormant">Dormant</SelectItem>
            <SelectItem value="at_risk">At Risk</SelectItem>
            <SelectItem value="new">New</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-32 h-8 text-xs" data-testid="sort-by">
            <SortAscending size={12} className="mr-1" />
            <SelectValue placeholder="Sort" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="created_at">Newest</SelectItem>
            <SelectItem value="last_active">Last Active</SelectItem>
            <SelectItem value="name">Name</SelectItem>
            <SelectItem value="users">Most Users</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <p className="text-[10px] text-muted-foreground mb-3">{filtered.length} tenant{filtered.length !== 1 ? "s" : ""} shown</p>

      {/* Tenant List */}
      <div className="space-y-3" data-testid="tenant-list">
        {filtered.map(t => {
          const limits = PLAN_LIMITS[t.plan] || {};
          const actBadge = ACTIVITY_BADGE[t.activity_status] || ACTIVITY_BADGE.new;
          const ActIcon = actBadge.icon;
          return (
            <div key={t.id} className={`border rounded-lg p-4 transition-colors ${t.is_master ? "border-indigo-300 dark:border-indigo-700 bg-indigo-50/30 dark:bg-indigo-950/10" : "border-border bg-card"}`} data-testid={`tenant-${t.slug}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h3 className="text-sm font-bold font-[Chivo]">{t.name}</h3>
                    {t.is_master && <Badge variant="outline" className="text-[9px] border-indigo-400 text-indigo-600">Master</Badge>}
                    <span className={`text-[9px] px-1.5 py-0.5 rounded font-medium ${PLAN_COLORS[t.plan] || ""}`}>{PLAN_LABELS[t.plan] || t.plan}</span>
                    <span className={`text-[9px] px-1.5 py-0.5 rounded font-medium ${STATUS_COLORS[t.status] || ""}`}>{t.status}</span>
                    <span className={`text-[9px] px-1.5 py-0.5 rounded font-medium inline-flex items-center gap-1 ${actBadge.color}`} data-testid={`activity-badge-${t.slug}`}>
                      <ActIcon size={9} weight="bold" /> {actBadge.label}
                    </span>
                    {healthMap[t.id] && (
                      <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold inline-flex items-center gap-1 ${healthMap[t.id].grade === "A" ? "bg-emerald-100 text-emerald-700" : healthMap[t.id].grade === "B" ? "bg-blue-100 text-blue-700" : healthMap[t.id].grade === "C" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`} data-testid={`health-badge-${t.slug}`}>
                        <Heartbeat size={9} weight="bold" /> {healthMap[t.id].score} ({healthMap[t.id].grade})
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-[10px] text-muted-foreground mb-2 flex-wrap">
                    <span>{t.slug}.necos.fm</span>
                    <span>&middot; Created {new Date(t.created_at).toLocaleDateString("en-GB")}</span>
                    <span className="flex items-center gap-1">&middot; <Clock size={9} /> Last active: {timeAgo(t.last_active)}</span>
                    {t.active_users_7d !== undefined && (
                      <span>&middot; {t.active_users_7d} active user{t.active_users_7d !== 1 ? "s" : ""} (7d)</span>
                    )}
                  </div>

                  {/* Usage */}
                  <div className="grid grid-cols-3 gap-3 max-w-md">
                    <UsageBar used={t.usage?.users || 0} limit={limits.users || -1} label="Users" />
                    <UsageBar used={t.usage?.projects || 0} limit={limits.projects || -1} label="Projects" />
                    <UsageBar used={t.usage?.suppliers || 0} limit={limits.suppliers || -1} label="Suppliers" />
                  </div>
                </div>

                <div className="flex items-center gap-1.5 shrink-0">
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setEditTenant({ ...t })} data-testid={`edit-tenant-${t.slug}`}>
                    <Pencil size={12} />
                  </Button>
                  {!t.is_master && (
                    <Button
                      variant="ghost" size="sm" className="h-7 w-7 p-0"
                      onClick={() => toggleStatus(t, t.status === "active" ? "suspended" : "active")}
                      data-testid={`toggle-tenant-${t.slug}`}
                    >
                      <Power size={12} className={t.status === "active" ? "text-emerald-500" : "text-red-500"} />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="text-center py-12 text-muted-foreground text-sm">
            No tenants match your filters
          </div>
        )}
      </div>

      {/* Edit Tenant Dialog */}
      <Dialog open={!!editTenant} onOpenChange={open => !open && setEditTenant(null)}>
        <DialogContent data-testid="edit-tenant-dialog">
          <DialogHeader><DialogTitle>Edit Tenant</DialogTitle></DialogHeader>
          {editTenant && (
            <div className="space-y-4">
              <div>
                <Label>Company Name</Label>
                <Input value={editTenant.name} onChange={e => setEditTenant({ ...editTenant, name: e.target.value })} className="mt-1" data-testid="edit-tenant-name" />
              </div>
              <div>
                <Label>Plan</Label>
                <Select value={editTenant.plan} onValueChange={v => setEditTenant({ ...editTenant, plan: v })}>
                  <SelectTrigger className="mt-1" data-testid="edit-tenant-plan"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {availablePlans.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={editTenant.status} onValueChange={v => setEditTenant({ ...editTenant, status: v })}>
                  <SelectTrigger className="mt-1" data-testid="edit-tenant-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={handleUpdate} className="w-full" data-testid="save-tenant-btn">Save Changes</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
